// ─── OPRF client-side primitives (RFC 9497, Mode 0x00) ────────────────────────
//
// Protocol: OPRF(ristretto255, SHA-512), base mode (no verifiability).
// Client performs: blind → send blinded input → receive evaluation → unblind → finalize.
// Server cannot learn the input; client cannot forge evaluations without the server key.
//
// Uses @noble/curves@1.9.7 — Point.hashToCurve via ristretto255_hasher, Fn scalar field.
// ─────────────────────────────────────────────────────────────────────────────

import { ristretto255, ristretto255_hasher } from "@noble/curves/ed25519";
import { sha512 } from "@noble/hashes/sha512";

const Fn = ristretto255.Point.Fn;
const RistPoint = ristretto255.Point;

// RFC 9497 §3.3.1: contextString = "OPRFV1-" || I2OSP(mode, 1) || "-" || identifier
// mode 0x00 = base OPRF, identifier = "ristretto255-SHA512"
const CONTEXT_STRING = new TextEncoder().encode(
  "OPRFV1-\x00-ristretto255-SHA512",
);

// HashToGroup DST = "HashToGroup-" || contextString
const HTG_DST: Uint8Array = (() => {
  const prefix = new TextEncoder().encode("HashToGroup-");
  const dst = new Uint8Array(prefix.length + CONTEXT_STRING.length);
  dst.set(prefix, 0);
  dst.set(CONTEXT_STRING, prefix.length);
  return dst;
})();

function concatBytes(...arrs: Uint8Array[]): Uint8Array {
  const total = arrs.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

function i2osp2(n: number): Uint8Array {
  return new Uint8Array([(n >> 8) & 0xff, n & 0xff]);
}

/**
 * Hash an arbitrary input to a ristretto255 group element.
 * DST = "HashToGroup-OPRFV1-\x00-ristretto255-SHA512"
 */
export function hashToGroup(
  input: Uint8Array,
): ReturnType<typeof ristretto255_hasher.hashToCurve> {
  return ristretto255_hasher.hashToCurve(input, { DST: HTG_DST });
}

/**
 * Generate a random non-zero scalar in the ristretto255 scalar field.
 * Uses 512 bits of randomness reduced mod Fn.ORDER.
 */
export function randomScalar(): bigint {
  const rand = new Uint8Array(64);
  crypto.getRandomValues(rand);
  const n = BigInt(
    "0x" +
      Array.from(rand).map((b) => b.toString(16).padStart(2, "0")).join(""),
  );
  return Fn.create(n);
}

/**
 * Blind the input for OPRF: compute r * HashToGroup(input).
 *
 * @param input - raw input bytes
 * @param presetScalar - optional 32-byte LE scalar for deterministic testing (KAT)
 * @returns blinded point bytes (32 bytes) and the blind scalar r
 */
export function oprfBlind(
  input: Uint8Array,
  presetScalar?: Uint8Array,
): { blinded: Uint8Array; blind: bigint } {
  const blind = presetScalar ? Fn.fromBytes(presetScalar) : randomScalar();
  const P = hashToGroup(input);
  const B = P.multiply(blind);
  return { blinded: B.toRawBytes(), blind };
}

/**
 * Unblind the server evaluation: compute r^-1 * E.
 *
 * @param evaluation - 32-byte serialized ristretto255 point from server
 * @param blind - the scalar r used during blinding
 * @returns unblinded point bytes (32 bytes)
 */
export function oprfUnblind(evaluation: Uint8Array, blind: bigint): Uint8Array {
  const E = RistPoint.fromBytes(evaluation);
  const blindInv = Fn.inv(blind);
  return E.multiply(blindInv).toRawBytes();
}

/**
 * Finalize the OPRF output per RFC 9497 §3.3.1:
 *   SHA-512(I2OSP(len(input), 2) || input || I2OSP(len(unblinded), 2) || unblinded || "Finalize")
 *
 * @param input - original raw input bytes
 * @param blind - the scalar r used during blinding
 * @param evaluation - 32-byte serialized ristretto255 point from server
 * @returns 64-byte OPRF output
 */
export async function oprfFinalize(
  input: Uint8Array,
  blind: bigint,
  evaluation: Uint8Array,
): Promise<Uint8Array> {
  const unblinded = oprfUnblind(evaluation, blind);
  const hashInput = concatBytes(
    i2osp2(input.length),
    input,
    i2osp2(unblinded.length),
    unblinded,
    new TextEncoder().encode("Finalize"),
  );
  return sha512(hashInput);
}
